import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';

import { trackAnalysisCreationPreviewError } from '../../../../analytics';

import { CreationPreviewClip } from '../../../../types';

interface Props {
  hlReelSrc: string;
  previewClips: CreationPreviewClip[];
  className?: string;
  isSample?: boolean;
  shouldPlay?: boolean;
}

const CreationPreview: React.FC<Props> = (props) => {
  const {
    hlReelSrc,
    previewClips,
    className,
    isSample = false,
    shouldPlay = true,
  } = props;

  const hlReelVideoRef = useRef<HTMLVideoElement | null>(null);
  const clipVideoRef = useRef<HTMLVideoElement | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const activeClipIndexRef = useRef<number | null>(null);

  const [activeClipIndex, setActiveClipIndex] = useState<number | null>(null);

  const [isVideoLoading, setIsVideoLoading] = useState(true);

  // play highlight reel, preload upcoming clips, and show clip overlays on schedule
  useEffect(() => {
    const hlReelVideo = hlReelVideoRef.current;

    if (!hlReelVideo || !shouldPlay) return;

    hlReelVideo.currentTime = 0;
    hlReelVideo.play().catch((err) => {
      console.error('CreationPreview hlReel play failed:', err);
      trackAnalysisCreationPreviewError(isSample);
    });

    const watchTime = () => {
      if (!hlReelVideo || hlReelVideo.paused) return;

      const currentTime = hlReelVideo.currentTime;
      const clipVideo = clipVideoRef.current;

      // find which clip should be visible now
      let nextActiveClipIndex: number | null = null;
      for (let i = 0; i < previewClips.length; i++) {
        const clip = previewClips[i];
        if (currentTime >= clip.reelStart && currentTime < clip.reelEnd) {
          nextActiveClipIndex = i;
          break;
        }
      }

      // preload upcoming clip (2 seconds before needed)
      const upcomingClipIndex = previewClips.findIndex(
        (c) => c.reelStart > currentTime && c.reelStart < currentTime + 2,
      );
      if (upcomingClipIndex !== -1 && clipVideo) {
        const upcomingClip = previewClips[upcomingClipIndex];
        if (clipVideo.src !== upcomingClip.src) {
          clipVideo.src = upcomingClip.src;
          clipVideo.currentTime = upcomingClip.clipStart;
          clipVideo.load();
        }
      }

      // update active clip if it changed
      if (nextActiveClipIndex !== activeClipIndexRef.current) {
        activeClipIndexRef.current = nextActiveClipIndex;
        setActiveClipIndex(nextActiveClipIndex);

        if (clipVideo)
          if (nextActiveClipIndex !== null) {
            const clip = previewClips[nextActiveClipIndex];

            if (clipVideo.src !== clip.src) clipVideo.src = clip.src;

            const seekAndPlay = () => {
              clipVideo.currentTime = clip.clipStart;
              clipVideo.play().catch((err) => {
                console.error('CreationPreview clip play failed:', err);
              });
              clipVideo.removeEventListener('canplay', seekAndPlay);
            };

            // if already buffered, play immediately; otherwise wait for canplay
            if (clipVideo.readyState >= 2) seekAndPlay();
            else clipVideo.addEventListener('canplay', seekAndPlay);
          } else clipVideo.pause();
      }

      animationFrameRef.current = requestAnimationFrame(watchTime);
    };

    animationFrameRef.current = requestAnimationFrame(watchTime);

    return () => {
      if (animationFrameRef.current)
        cancelAnimationFrame(animationFrameRef.current);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewClips, isSample, shouldPlay]);

  return (
    <div
      className={classNames(
        'relative w-full flex flex-col items-center rounded-md overflow-hidden',
        { 'aspect-video': !className?.includes('aspect-') },
        className,
      )}
    >
      {(isVideoLoading || !shouldPlay) && (
        <div className="w-full h-full skeleton" />
      )}

      {/* highlight reel */}
      <video
        ref={hlReelVideoRef}
        className={classNames(
          'w-full h-full object-contain',
          isVideoLoading || !shouldPlay ? 'hidden' : '',
        )}
        src={hlReelSrc}
        muted
        playsInline
        loop
        preload="auto"
        onCanPlay={() => setIsVideoLoading(false)}
        onError={(e) => console.error('CreationPreview hlReel error:', e)}
      />

      {/* clip overlay */}
      <video
        ref={clipVideoRef}
        className={classNames(
          'absolute top-0 left-0 w-full h-full object-contain',
          activeClipIndex === null ? 'hidden' : '',
        )}
        muted
        playsInline
        onError={(e) => console.error('CreationPreview clip error:', e)}
      />
    </div>
  );
};

export default CreationPreview;
